import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle } from "lucide-react";
import { COURSES } from "../lib/data";
import { useSEO } from "../lib/useSEO";
import { usePlanning } from "../lib/usePlanning";
import { coursesSchema } from "../lib/schema";
import { CourseVideo } from "../components/CourseVideo";
import { thumb, thumbSet, fallbackToFull } from "../lib/imageThumb";
import { BlobShape } from "../components/decor/BlobShape";
import { DotsPattern } from "../components/decor/DotsPattern";
import { useRevealOnScroll } from "../lib/useReveal";

const TABS = ["Tous", "Hip-Hop", "Girly", "Compétition", "Heels", "Comédie musicale", "Parents-enfants", "Ballet", "Contemporain", "Dancehall", "Break Dance", "Salsa", "Zumba", "Eveil", "Reggaeton", "Cours privés"];

export default function Cours() {
  const { courses: planningCourses } = usePlanning();
  const coursesJsonLd = useMemo(() => coursesSchema(planningCourses), [planningCourses]);
  useSEO({
    title: "Nos cours de danse, chant et comédie musicale à Cugy VD",
    description: "Découvrez toutes nos disciplines : hip-hop, comédie musicale, ballet, heels, salsa, dancehall, zumba, théâtre, chant. Pour enfants dès 2 ans, ados, adultes et seniors à Cugy VD.",
    canonical: "/cours",
    jsonLd: coursesJsonLd,
  });
  const [tab, setTab] = useState("Tous");
  const filtered = tab === "Tous" ? COURSES : COURSES.filter((c) => c.category === tab);
  useRevealOnScroll([tab]);

  return (
    <div data-testid="page-cours" className="pt-32 pb-24 relative overflow-hidden mesh-bg">
      <BlobShape variant="soft" size="xl" position="top-40 -right-40" opacity={0.14} animation="float" />
      <BlobShape variant="tint" size="lg" position="top-[80vh] -left-40" opacity={0.5} animation="drift" />
      <DotsPattern position="top-[45vh] right-10" size={160} opacity={0.12} />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
        <div className="eyebrow mb-3">Nos disciplines</div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight leading-tight">
          Découvre<br /><span style={{ color: "var(--primary)" }}>nos cours</span>
        </h1>

        <div className="flex flex-wrap gap-2 mt-10">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setTab(t)}
              className={`pill ${tab === t ? "active" : ""}`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
          {filtered.map((c) => (
            <CourseCard key={c.slug} course={c} />
          ))}
        </div>
      </div>

      {/* CTAs — anciennement section professeurs, désormais séparée sur /professeurs */}
      <section className="section-soft py-24 mt-24" data-testid="cours-ctas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <div className="eyebrow mb-3">Envie d'aller plus loin&nbsp;?</div>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight">
            Découvre l'équipe et le <span style={{ color: "var(--primary)" }}>planning</span>
          </h2>
          <p className="mt-4 text-lg text-[var(--muted)] max-w-2xl mx-auto">
            Chaque cours est porté par un·e professeur·e formé·e auprès des meilleurs. Trouve le créneau qui te convient dans notre planning hebdomadaire.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/professeurs" data-testid="cours-cta-professeurs" className="btn-primary" style={{ padding: "14px 28px" }}>
              Découvre notre équipe <ArrowRight size={16} />
            </Link>
            <Link to="/planning" data-testid="cours-cta-planning" className="btn-outline" style={{ padding: "14px 28px" }}>
              Voir le planning hebdomadaire
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

/**
 * Carte cours : l'iframe d'aperçu n'est montée qu'au PREMIER survol, puis conservée
 * (les survols suivants sont instantanés). La monter d'emblée sur chaque carte faisait
 * charger 10 lecteurs YouTube à l'ouverture de /cours, soit 2,5 Mo avant la première
 * interaction — c'était la cause principale de la lenteur d'affichage.
 */
function CourseCard({ course }) {
  const [hover, setHover] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [canHover, setCanHover] = useState(true);
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  useEffect(() => {
    // Détecte les devices tactiles (pas de vrai hover) — on skip alors l'aperçu vidéo.
    setCanHover(window.matchMedia("(hover: hover) and (pointer: fine)").matches);
    return () => {
      clearTimeout(enterTimer.current);
      clearTimeout(leaveTimer.current);
    };
  }, []);
  const hasVideo = !!course.video;
  const showVideoPreview = hasVideo && canHover;
  // L'iframe est montée au survol et DÉMONTÉE à la sortie de la souris : c'est la seule
  // garantie absolue qu'aucun son ne persiste. Le petit délai à l'entrée évite de
  // charger dix lecteurs quand la souris balaie la grille ; le court sursis à la sortie
  // évite de détruire et recharger le lecteur quand on repasse aussitôt sur la carte
  // (YouTube finissait par refuser de rejouer).
  const onEnter = () => {
    if (!showVideoPreview) return;
    clearTimeout(leaveTimer.current);
    clearTimeout(enterTimer.current);
    setHover(true);
    enterTimer.current = setTimeout(() => setMounted(true), 140);
  };
  const onLeave = () => {
    if (!showVideoPreview) return;
    clearTimeout(enterTimer.current);
    setHover(false); // coupe le son immédiatement via l'API du lecteur
    setVideoPlaying(false);
    leaveTimer.current = setTimeout(() => setMounted(false), 250);
  };
  return (
    <Link
      to={`/cours/${course.slug}`}
      data-testid={`cours-item-${course.slug}`}
      className="card group flex flex-col"
      style={{ padding: 0, overflow: "hidden" }}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        {course.portrait ? (
          <>
            {/* Fond flou pour photos verticales */}
            <img loading="lazy" decoding="async"
              src={thumb(course.image)}
              srcSet={thumbSet(course.image)}
              sizes="(max-width: 768px) 100vw, 33vw"
              onError={fallbackToFull(course.image)}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "blur(24px)", transform: "scale(1.1)" }}
            />
            <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.15)" }} aria-hidden="true" />
            {/* Photo verticale entière (contain) */}
            <img loading="lazy" decoding="async"
              src={thumb(course.image)}
              srcSet={thumbSet(course.image)}
              sizes="(max-width: 768px) 100vw, 33vw"
              onError={fallbackToFull(course.image)}
              alt={course.name}
              className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform duration-500 z-[1]"
            />
          </>
        ) : (
          <img loading="lazy" decoding="async"
            src={thumb(course.image)}
            srcSet={thumbSet(course.image)}
            sizes="(max-width: 768px) 100vw, 33vw"
            onError={fallbackToFull(course.image)}
            alt={course.name}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        )}
        {hasVideo && (
          <>
            {/* Badge vidéo — visible sur mobile (pas de hover) pour signaler qu'il y a une vidéo. */}
            <div
              className="md:hidden absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold shadow-md pointer-events-none"
              style={{ background: "rgba(255,255,255,0.95)", color: "var(--primary)" }}
              data-testid={`video-badge-${course.slug}`}
            >
              <PlayCircle size={14} /> Vidéo
            </div>
            {showVideoPreview && mounted && (
              <div
                className="absolute inset-0 overflow-hidden transition-opacity duration-300 z-[2] bg-black"
                style={{ opacity: videoPlaying && hover ? 1 : 0, pointerEvents: "none" }}
                aria-hidden={!videoPlaying}
              >
                <CourseVideo
                  video={course.video}
                  mode="preview"
                  title={`Aperçu ${course.name}`}
                  wantSound={hover}
                  onPlaying={() => setVideoPlaying(true)}
                />
              </div>
            )}
          </>
        )}
      </div>
      <div className="p-7 flex-1 flex flex-col">
        <div className="eyebrow text-[10px]">{course.category}</div>
        <h3 className="font-display text-2xl mt-2">{course.name}</h3>
        <p className="text-[var(--muted)] text-sm mt-3 leading-relaxed flex-1">{course.desc}</p>
        <span className="inline-flex items-center gap-1 text-sm font-medium mt-5" style={{ color: "var(--primary)" }}>
          En savoir plus <ArrowRight size={14} />
        </span>
      </div>
    </Link>
  );
}
